import type { Stats, StatsGroupBy } from '@/domain/types'
import { useApiQuery, type QueryState } from '@/api/useApiQuery'

/**
 * `GET /api/initiatives/stats?groupBy=…`.
 *
 * Replaces the five `pageSize=1` queries this hook used to fire to fake
 * per-status counts. That trick was fine while "how many are there" was the
 * only question the home band asked; the funnel board wants blocked-reason
 * aggregates and per-BU override counts, and no amount of counting list
 * pages produces those.
 *
 * One request, exact numbers at any dataset size, and the server does the
 * counting it was already going to do.
 */
export function useInitiativeStats(
  groupBy: StatsGroupBy = 'stage',
  refreshKey: number = 0,
): QueryState<Stats> {
  return useApiQuery<Stats>(`/api/initiatives/stats?groupBy=${groupBy}`, refreshKey)
}

/**
 * The stages the home band reports, in funnel order.
 *
 * `Operate` is included — unlike the old band, which hid `Cancelled`
 * because a cancelled count next to "24 AI initiatives" read as a
 * scoreboard. Operate is the opposite: things that made it all the way
 * through are the number the band exists to show.
 */
export const HOME_STAGE_CODES = [
  'Idea',
  'Shaped',
  'Experiment',
  'Pilot',
  'Scaling',
  'Operate',
] as const

export type HomeStageCode = (typeof HOME_STAGE_CODES)[number]

/** Count for one stage, or null while loading / on error. */
export function stageCount(stats: QueryState<Stats>, stageCode: string): number | null {
  if (stats.status !== 'success') return null
  return stats.data.groups.find(g => g.key === stageCode)?.count ?? 0
}
